import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import Modal from '../components/overlay/Modal';
import { Tooltip } from 'antd';
import { MdDeleteOutline } from 'react-icons/md';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import {
  ConversionChangeset, ConversionValues, SizeConversionResponse, SizeConversionRow,
} from '../models/size.conversion';
import { getSizeConversions, saveSizeConversions } from '../requests/size.conversion.request';

type FieldErrors = Partial<Record<keyof ConversionValues, string>>;
type DraftRow = { key: string; values: ConversionValues; expected?: SizeConversionRow };
type TableSettings = Pick<ConversionValues, 'model' | 'gender' | 'country'>;
type DraftTable = TableSettings & {
  key: string;
  group?: string;
  settingErrors?: FieldErrors;
  rows: DraftRow[];
  entryVersion: number;
  removed: SizeConversionRow[];
  errors: Record<string, FieldErrors>;
  message?: string;
  conflict?: boolean;
};

const fields: (keyof ConversionValues)[] = ['model', 'gender', 'original_size', 'converted_size', 'country'];
const rowFields: (keyof ConversionValues)[] = ['original_size', 'converted_size'];
const settingFields: (keyof TableSettings)[] = ['model', 'gender', 'country'];
const genders = ['*', 'u', 'f', 'm'];
const labels: Record<keyof ConversionValues, string> = {
  model: 'Model', gender: 'Gender', original_size: 'Original size', converted_size: 'Converted size', country: 'Country',
};
const snapshot = (row: ConversionValues): ConversionValues => ({
  model: row.model, gender: row.gender, original_size: row.original_size,
  converted_size: row.converted_size, country: row.country,
});
const sameValues = (first: ConversionValues, second: ConversionValues) => fields.every(field => first[field] === second[field]);
const groupKey = (row: TableSettings) => JSON.stringify([row.country, row.model, row.gender]);
const tableDirty = (table: DraftTable) => !table.group || table.removed.length > 0 || table.rows.some(
  row => !row.expected || !sameValues(row.values, row.expected),
);

const sortTables = (tables: DraftTable[]): DraftTable[] => [...tables].sort((first, second) => {
  for (const field of settingFields) {
    const comparison = (first[field] ?? '').localeCompare(second[field] ?? '');
    if (comparison) return comparison;
  }
  return 0;
});

const groupRows = (rows: SizeConversionRow[]): DraftTable[] => {
  const groups = new Map<string, DraftTable>();
  rows.forEach(row => {
    const group = groupKey(row);
    if (!groups.has(group)) groups.set(group, {
      key: `group:${group}`, group, country: row.country, model: row.model, gender: row.gender,
      rows: [], entryVersion: 0, removed: [], errors: {},
    });
    groups.get(group)!.rows.push({ key: `row:${row.id}`, values: snapshot(row), expected: row });
  });
  return sortTables(Array.from(groups.values()));
};

const mergeSavedTables = (tables: DraftTable[], savedKey: string, rows: SizeConversionRow[]): DraftTable[] => {
  const serverRows = new Map(rows.map(row => [row.id, row]));
  const pending = tables.filter(table => table.key !== savedKey && tableDirty(table)).map(table => {
    const changed = [...table.rows.flatMap(row => row.expected ? [row.expected] : []), ...table.removed].some(
      expected => !serverRows.has(expected.id) || !sameValues(expected, serverRows.get(expected.id)!),
    );
    return changed ? { ...table, rows: [...table.rows], conflict: true, message: 'Saved data changed while this table had edits. Reload before saving this table.' } : { ...table, rows: [...table.rows] };
  });
  const heldIds = new Set(pending.flatMap(table => [
    ...table.rows.flatMap(row => row.expected ? [row.expected.id] : []),
    ...table.removed.map(row => row.id),
  ]));
  const fresh = groupRows(rows.filter(row => !heldIds.has(row.id)));
  fresh.forEach(table => {
    const destination = pending.find(candidate => candidate.group === table.group && groupKey(candidate) === groupKey(table));
    if (destination) destination.rows = [...destination.rows, ...table.rows];
    else {
      while (pending.some(candidate => candidate.key === table.key)) table.key += ':saved';
      pending.push(table);
    }
  });
  return sortTables(pending);
};

const normalized = (values: ConversionValues): ConversionValues => ({
  model: values.model.trim(), gender: values.gender === null ? '*' : values.gender.trim(),
  original_size: values.original_size.trim(), converted_size: values.converted_size.trim(), country: values.country,
});

const buildChangeset = (table: DraftTable): ConversionChangeset => ({
  added: table.rows.filter(row => !row.expected).map(row => ({ client_key: row.key, values: normalized(row.values) })),
  updated: table.rows.filter(row => row.expected && !sameValues(row.values, row.expected)).map(row => ({
    id: row.expected!.id, expected: snapshot(row.expected!), values: normalized(row.values),
  })),
  removed: table.removed.map(row => ({ id: row.id, expected: snapshot(row) })),
});

const validationErrors = (table: DraftTable, countries: string[]): Record<string, FieldErrors> => {
  const errors: Record<string, FieldErrors> = {};
  table.rows.forEach(row => {
    if (row.expected && sameValues(row.values, row.expected)) return;
    const values = normalized(row.values);
    const rowErrors: FieldErrors = {};
    fields.forEach(field => {
      const value = values[field] || '';
      const maximum = field === 'model' ? 255 : field === 'gender' ? 1 : 64;
      if (field === 'country') {
        if (!countries.includes(value)) rowErrors.country = 'Choose a country.';
      } else if (!value) rowErrors[field] = `${labels[field]} is required.`;
      else if (value.length > maximum) rowErrors[field] = `Use ${maximum} characters or fewer.`;
    });
    if (Object.keys(rowErrors).length) errors[row.key] = rowErrors;
  });
  return errors;
};

const errorMessage = (error: unknown): string => {
  const data = (error as { response?: { data?: { message?: string; detail?: string; non_field_errors?: string[] } } }).response?.data;
  return data?.message || data?.detail || data?.non_field_errors?.join(' ') || 'Unable to complete the request. Please retry.';
};

const SizeConversion = () => {
  const { id: shopId } = useParams();
  const [data, setData] = useState<SizeConversionResponse | null>(null);
  const [tables, setTables] = useState<DraftTable[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [saving, setSaving] = useState<string | null>(null);
  const [success, setSuccess] = useState('');
  const [reload, setReload] = useState(0);
  const [confirm, setConfirm] = useState<{ action: 'save' | 'reload'; key?: string } | null>(null);
  const generation = useRef(0);
  const sequence = useRef(0);
  const submitting = useRef(false);
  const tableBodies = useRef(new Map<string, HTMLTableSectionElement>());
  const pendingFocus = useRef<{ tableKey: string; field: keyof ConversionValues; shopId: string | undefined } | null>(null);

  useLayoutEffect(() => {
    const request = pendingFocus.current;
    pendingFocus.current = null;
    if (!request || request.shopId !== shopId) return;
    // The input is in s-text-field's shadow root; focusing the host doesn't move focus into it in every browser.
    tableBodies.current.get(request.tableKey)?.lastElementChild
      ?.querySelector(`[data-field="${request.field}"] s-text-field`)?.shadowRoot?.querySelector('input')?.focus();
  }, [tables, shopId]);

  useEffect(() => {
    const current = ++generation.current;
    submitting.current = false;
    setData(null);
    setTables([]);
    setSaving(null);
    setSuccess('');
    setConfirm(null);
    setLoadError('');
    setLoading(true);
    if (!shopId) {
      setLoadError('Shop ID is missing.');
      setLoading(false);
      return;
    }
    getSizeConversions(shopId).then(response => {
      if (generation.current !== current) return;
      setData(response);
      setTables(groupRows(response.rows));
    }).catch(error => {
      if (generation.current === current) setLoadError(errorMessage(error));
    }).finally(() => {
      if (generation.current === current) setLoading(false);
    });
    return () => { generation.current += 1; };
  }, [shopId, reload]);

  const newRow = (settings: TableSettings = { country: '', model: '*', gender: '*' }): DraftRow => ({
    key: `draft-row:${++sequence.current}`,
    values: { ...settings, original_size: '', converted_size: '' },
  });
  const changeTable = (key: string, transform: (table: DraftTable) => DraftTable) => {
    setSuccess('');
    setTables(current => current.map(table => table.key === key ? transform(table) : table));
  };

  const changeSetting = (key: string, field: keyof TableSettings, value: string) => {
    changeTable(key, table => ({
      ...table, [field]: value,
      rows: table.rows.map(row => ({ ...row, values: { ...row.values, [field]: value } })),
      errors: {}, settingErrors: {}, message: undefined,
    }));
  };

  const changeRow = (tableKey: string, rowKey: string, field: keyof ConversionValues, value: string) => {
    const currentRow = tables.find(table => table.key === tableKey)?.rows.find(row => row.key === rowKey);
    const values = currentRow && { ...currentRow.values, [field]: value };
    const activeInput = document.activeElement;
    if (currentRow && !currentRow.expected && values?.original_size === '' && values.converted_size === ''
      && tableBodies.current.get(tableKey)?.contains(activeInput)
      && activeInput?.closest('tr')?.getAttribute('data-row-key') === rowKey) {
      pendingFocus.current = { tableKey, field, shopId };
    }
    changeTable(tableKey, table => {
      const entryKey = `${table.key}:entry:${table.entryVersion}`;
      if (rowKey !== entryKey) return {
        ...table, rows: table.rows.map(row => row.key === rowKey ? { ...row, values: { ...row.values, [field]: value } } : row)
          .filter(row => row.expected || row.values.original_size !== '' || row.values.converted_size !== ''),
        errors: {}, message: undefined,
      };
      if (!value) return table;
      const gender = table.gender ?? '*';
      const row: DraftRow = {
        key: entryKey,
        values: { country: table.country, model: table.model, gender, original_size: '', converted_size: '', [field]: value },
      };
      return {
        ...table, gender, entryVersion: table.entryVersion + 1,
        rows: [...table.rows.map(candidate => ({ ...candidate, values: { ...candidate.values, gender } })), row],
        errors: {}, settingErrors: {}, message: undefined,
      };
    });
  };

  const duplicateTable = (table: DraftTable) => {
    if (submitting.current || !table.rows.length) return;
    const settings = { model: table.model, gender: table.gender ?? '*', country: '' };
    const rows = table.rows.map(source => {
      const row = newRow(settings);
      return { ...row, values: { ...row.values, original_size: source.values.original_size } };
    });
    const duplicate: DraftTable = {
      ...settings, key: `draft-table:${++sequence.current}`, rows, entryVersion: 0, removed: [], errors: {},
    };
    setSuccess('');
    setTables(current => [duplicate, ...current]);
  };

  const saveTable = async (table: DraftTable, confirmed = false) => {
    if (!shopId || !data || submitting.current || table.conflict) return;
    if (table.rows.length) {
      const settingErrors: FieldErrors = {};
      if (!data.countries.includes(table.country as typeof data.countries[number])) settingErrors.country = 'Choose a country.';
      if (!table.model.trim()) settingErrors.model = 'Model is required.';
      else if (table.model.trim().length > 255) settingErrors.model = 'Use 255 characters or fewer.';
      if (table.gender !== null && !table.gender.trim()) settingErrors.gender = 'Gender is required.';
      else if (table.gender && table.gender.trim().length > 1) settingErrors.gender = 'Use 1 character or fewer.';
      if (Object.keys(settingErrors).length) {
        changeTable(table.key, current => ({ ...current, settingErrors }));
        return;
      }
    }
    const errors = validationErrors(table, data.countries);
    if (Object.keys(errors).length) {
      changeTable(table.key, current => ({ ...current, errors, message: 'Correct the highlighted fields before saving.' }));
      return;
    }
    if (!confirmed && table.rows.length === 0 && table.removed.length > 0) {
      setConfirm({ action: 'save', key: table.key });
      return;
    }
    const changes = buildChangeset(table);
    const currentGeneration = generation.current;
    submitting.current = true;
    setSaving(table.key);
    changeTable(table.key, current => ({ ...current, errors: {}, settingErrors: {}, message: undefined }));
    try {
      const response = await saveSizeConversions(shopId, changes);
      if (generation.current !== currentGeneration) return;
      setData(response);
      setTables(current => mergeSavedTables(current, table.key, response.rows));
      setSuccess('Conversion table saved.');
    } catch (error) {
      if (generation.current !== currentGeneration) return;
      const response = (error as { response?: { status?: number; data?: Record<string, unknown> } }).response;
      const fieldErrors: Record<string, FieldErrors> = {};
      (['added', 'updated'] as const).forEach(operation => {
        const operationErrors = response?.data?.[operation] as Record<string, { values?: Record<string, string[]> }> | undefined;
        Object.entries(operationErrors || {}).forEach(([index, detail]) => {
          const change = changes[operation][Number(index)];
          const row = change && (operation === 'added'
            ? table.rows.find(candidate => candidate.key === (change as ConversionChangeset['added'][number]).client_key)
            : table.rows.find(candidate => candidate.expected?.id === (change as ConversionChangeset['updated'][number]).id));
          if (!row) return;
          fields.forEach(field => {
            if (detail.values?.[field]) fieldErrors[row.key] = {
              ...fieldErrors[row.key], [field]: detail.values[field].join(' '),
            };
          });
        });
      });
      changeTable(table.key, current => ({
        ...current, errors: fieldErrors,
        settingErrors: Object.fromEntries(settingFields.map(field => [field, Object.values(fieldErrors).find(errors => errors[field])?.[field]])),
        conflict: response?.status === 409,
        message: response?.status === 400 ? 'The conversion table was not saved. Check its values and remove any exact duplicates.' : errorMessage(error),
      }));
    } finally {
      if (generation.current === currentGeneration) {
        submitting.current = false;
        setSaving(null);
      }
    }
  };

  const ready = !loading && data?.shop.shop_id === shopId;

  return (
    <Container>
      <PageHeader>
        <div><h1>Size Conversion</h1>{ready && data && <Context>
          <span>Shop ID <strong>{data.shop.shop_id}</strong></span>
          <span>Brand <strong>{data.brand.brand}</strong></span>
          <span>Brand ID <strong>{data.brand.id}</strong></span>
        </Context>}</div>
        <Commands>
          <s-button icon='refresh' disabled={!ready || !!saving} onClick={() => {
            if (tables.some(tableDirty)) setConfirm({ action: 'reload' });
            else setReload(value => value + 1);
          }}>Reload</s-button>
          <s-button icon='plus' variant='primary' disabled={!ready || !!saving} onClick={() => {
            const key = `draft-table:${++sequence.current}`;
            setSuccess('');
            setTables(current => [{ key, country: '', model: '*', gender: '*', rows: [], entryVersion: 0, removed: [], errors: {} }, ...current]);
          }}>Add conversion</s-button>
        </Commands>
      </PageHeader>
      {loading && <Loading role='status'><s-spinner /><span>Loading conversions</span></Loading>}
      {!loading && loadError && <s-banner tone='critical' heading='Conversions unavailable'>
        <p>{loadError}</p><s-button onClick={() => setReload(value => value + 1)}>Retry</s-button>
      </s-banner>}
      {success && <Notice role='status'>{success}</Notice>}
      {ready && tables.length === 0 && <Empty>No size conversions</Empty>}
      {ready && tables.map((table, tableIndex) => {
        const number = tableIndex + 1;
        const entry: DraftRow = {
          key: `${table.key}:entry:${table.entryVersion}`,
          values: { country: table.country, model: table.model, gender: table.gender, original_size: '', converted_size: '' },
        };
        return <ConversionSection key={table.key} aria-label={`Conversion table ${number}`}>
          <TableHeader>
            <div><h2>{table.group ? `${table.country || 'Select country'} / ${table.model} / ${table.gender ?? '*'}` : `New conversion ${number}`}</h2>
              <TableMeta>{table.rows.length} {table.rows.length === 1 ? 'row' : 'rows'}{tableDirty(table) ? ' / Unsaved changes' : ''}</TableMeta></div>
            <Commands>
              <s-button icon='duplicate' accessibilityLabel={`Duplicate table ${number}`} disabled={!!saving || !table.rows.length} onClick={() => duplicateTable(table)}>
                Duplicate
              </s-button>
              <s-button icon='undo' disabled={!!saving || !tableDirty(table)} onClick={() => {
                setSuccess('');
                setTables(current => mergeSavedTables(current, table.key, data!.rows));
              }}>Discard</s-button>
              <s-button icon='save' variant='primary' loading={saving === table.key} disabled={!!saving || !tableDirty(table) || table.conflict || (!table.rows.length && !table.removed.length)} onClick={() => saveTable(table)}>
                Save
              </s-button>
            </Commands>
          </TableHeader>
          <SettingsRow>
            {settingFields.map(field => <SettingControl key={field} $field={field}>
              <SettingLabel aria-hidden>{labels[field]}</SettingLabel>
              {field === 'country' ? <s-select
                label={`Country, table ${number}`} labelAccessibilityVisibility='exclusive'
                // A placeholder, not an s-option: s-option drops an empty value and uses its label instead.
                placeholder='Select country' value={table.country} disabled={!!saving}
                error={table.settingErrors?.country}
                onChange={event => changeSetting(table.key, field, event.currentTarget.value)}
              >
                {data!.countries.map(country => <s-option key={country} value={country}>{country}</s-option>)}
              </s-select> : field === 'gender' ? <s-select
                label={`Gender, table ${number}`} labelAccessibilityVisibility='exclusive'
                value={table.gender ?? '*'} disabled={!!saving}
                error={table.settingErrors?.gender}
                onChange={event => changeSetting(table.key, field, event.currentTarget.value)}
              >
                {genders.map(gender => <s-option key={gender} value={gender}>{gender}</s-option>)}
                {table.gender !== null && !genders.includes(table.gender)
                  && <s-option value={table.gender} disabled>{table.gender}</s-option>}
              </s-select> : <s-text-field
                label={`${labels[field]}, table ${number}`} labelAccessibilityVisibility='exclusive' autocomplete='off'
                value={table[field]} disabled={!!saving} maxLength={255}
                error={table.settingErrors?.[field]}
                onInput={event => changeSetting(table.key, field, event.currentTarget.value)}
              />}
            </SettingControl>)}
          </SettingsRow>
          {table.message && <s-banner tone='critical'><p>{table.message}</p></s-banner>}
          <TableScroll><Table>
            <thead><tr>{rowFields.map(field => <th key={field} scope='col'>{labels[field]}</th>)}<th scope='col'><Hidden>Remove row</Hidden></th></tr></thead>
            <tbody ref={element => {
              if (element) tableBodies.current.set(table.key, element);
              else tableBodies.current.delete(table.key);
            }}>{[...table.rows, entry].map((row, rowIndex) => <tr key={row.key} data-row-key={row.key}>
              {rowFields.map(field => <td key={field} data-field={field}><s-text-field
                label={`${labels[field]}, row ${rowIndex + 1}, table ${number}`} labelAccessibilityVisibility='exclusive'
                value={row.values[field] ?? '*'} autocomplete='off' disabled={!!saving}
                maxLength={64}
                error={table.errors[row.key]?.[field]}
                onInput={event => changeRow(table.key, row.key, field, event.currentTarget.value)}
              /></td>)}
              <td>{row.key !== entry.key && <Tooltip title='Remove row' trigger={['hover', 'focus']} styles={{ root: { pointerEvents: 'none' } }}>
                <IconButton type='button' disabled={!!saving} aria-label={`Remove row ${rowIndex + 1}, table ${number}`} onClick={() => changeTable(table.key, current => ({
                  ...current, rows: current.rows.filter(candidate => candidate.key !== row.key),
                  removed: row.expected ? [...current.removed, row.expected] : current.removed, errors: {}, message: undefined,
                }))}><MdDeleteOutline aria-hidden /></IconButton>
              </Tooltip>}</td>
            </tr>)}</tbody>
          </Table></TableScroll>
          {table.rows.length === 0 && <TableMeta>No rows</TableMeta>}
        </ConversionSection>;
      })}
      <Modal open={!!confirm} onClose={() => setConfirm(null)} heading={confirm?.action === 'reload' ? 'Discard unsaved changes?' : 'Remove all rows?'}
        primaryAction={{ content: confirm?.action === 'reload' ? 'Discard and reload' : 'Remove rows', destructive: true, onAction: () => {
          const action = confirm;
          setConfirm(null);
          if (action?.action === 'reload') setReload(value => value + 1);
          else {
            const table = tables.find(candidate => candidate.key === action?.key);
            if (table) saveTable(table, true);
          }
        } }} secondaryActions={[{ content: 'Cancel', onAction: () => setConfirm(null) }]}>
        <p>{confirm?.action === 'reload' ? 'Unsaved changes in all conversion tables will be discarded.' : 'Saving will delete all persisted rows removed from this table.'}</p>
      </Modal>
    </Container>
  );
};

export default SizeConversion;

const Container = styled.div`width: 100%; min-width: 0;`;
const PageHeader = styled.header`
  display: flex; justify-content: space-between; align-items: flex-start; flex-wrap: wrap; gap: 16px; margin-bottom: 24px;
  h1 { font-size: 24px; line-height: 32px; margin: 0 0 8px; font-weight: 600; }
`;
const Context = styled.div`display: flex; flex-wrap: wrap; gap: 8px 20px; color: #60646c; font-size: 13px; strong { color: #30343b; margin-left: 6px; overflow-wrap: anywhere; }`;
const Commands = styled.div`display: flex; flex-wrap: wrap; align-items: center; gap: 8px;`;
const SettingsRow = styled.div`display: flex; flex-wrap: wrap; align-items: flex-start; gap: 12px; margin-bottom: 16px;`;
const SettingControl = styled.div<{ $field: keyof TableSettings }>`width: ${props => props.$field === 'model' ? '240px' : props.$field === 'gender' ? '100px' : '150px'}; max-width: 100%;`;
const SettingLabel = styled.span`display: block; font-size: 12px; color: #60646c; margin-bottom: 4px;`;
const Loading = styled.div`display: flex; gap: 12px; align-items: center; padding: 24px 0;`;
const Notice = styled.div`padding: 12px 16px; background: #e5f5ec; color: #18563b; border-left: 3px solid #008060; margin-bottom: 16px;`;
const Empty = styled.p`padding: 32px 0; color: #60646c;`;
const ConversionSection = styled.section`padding: 20px 0; border-top: 1px solid #dce0e4; min-width: 0;`;
const TableHeader = styled.div`display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 12px; margin-bottom: 16px; h2 { font-size: 16px; font-weight: 600; line-height: 24px; margin: 0; overflow-wrap: anywhere; }`;
const TableMeta = styled.p`font-size: 13px; color: #60646c; margin: 4px 0 0;`;
const TableScroll = styled.div`max-width: 100%; overflow-x: auto; margin-top: 12px;`;
const Table = styled.table`
  width: 100%; min-width: 400px; table-layout: fixed; border-collapse: collapse;
  th { text-align: left; font-weight: 500; color: #60646c; font-size: 12px; padding: 0 8px 8px; }
  td { padding: 6px 8px; vertical-align: top; border-top: 1px solid #edf0f2; }
  th:last-child { width: 48px; }
`;
const IconButton = styled.button`
  display: inline-flex; align-items: center; justify-content: center; width: 36px; height: 36px;
  padding: 0; border: 1px solid transparent; border-radius: 4px; background: transparent; color: #60646c; cursor: pointer;
  svg { width: 20px; height: 20px; } &:hover { background: #fbeae8; color: #b42318; }
  &:focus-visible { outline: 2px solid #008060; outline-offset: 2px; } &:disabled { opacity: 0.5; cursor: default; }
`;
const Hidden = styled.span`position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0,0,0,0); white-space: nowrap; border: 0;`;